import React from "react";
import styles from "./DisplayDNA.module.css";

/**
 * Render stacked feature lanes for a given row.
 * Props:
 *  - row: { start, end, rowIndex, textTop, textBottom }
 *  - features: [{ id, name, color?, direction?, start, end }]
 *  - charAdvancePx: number (per-character advance in px)
 *  - onSelectRange: fn({start,end})
 */
export default function FeatureLanes({
  row,
  features = [],
  charAdvancePx,
  onSelectRange,
}) {
  const lanes = React.useMemo(
    () => computeFeatureTracksForRow(row, features),
    [row, features]
  );

  if (!lanes.length) return null;

  const adv = Math.max(1, charAdvancePx || 1);

  return (
    <div className={styles.featuresBlock} style={{ "--lanes": lanes.length }}>
      {lanes.map((lane, laneIdx) => (
        <div
          key={`lane-${laneIdx}`}
          className={styles.featureLaneRow}
          style={{
            top: `calc(${laneIdx} * (var(--feature-h) + var(--feature-gap)))`,
          }}
        >
          {lane.map((f) => {
            const count = f.e - f.s + 1;
            const leftPxR = Math.round(f.s * adv);
            const widthPxR = Math.max(1, Math.round(count * adv));

            return (
              <div
                key={`${f.id}-${row.rowIndex}`}
                className={styles.featureBar}
                title={f.name}
                style={{
                  left: `${leftPxR}px`,
                  width: `${widthPxR}px`,
                  background: f.color,
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSelectRange?.({ start: f.absStart, end: f.absEnd });
                }}
              >
                {f.direction === "reverse" && (
                  <span className={styles.arrow} aria-hidden="true">
                    ◀
                  </span>
                )}
                <span className={styles.featureLabel} title={f.name}>
                  {f.name}
                </span>
                {f.direction === "forward" && (
                  <span className={styles.arrow} aria-hidden="true">
                    ▶
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Greedy interval graph coloring per row (keeps feature direction). */
function computeFeatureTracksForRow(row, features) {
  const rowStart = row.start;
  const rowEndIncl = row.end - 1;

  const overlapping = features
    .filter((f) => !(f.end < rowStart || f.start > rowEndIncl))
    .map((f) => ({
      id: f.id,
      name: f.name,
      color: f.color || "#3B82F6",
      direction: f.direction || "none",
      absStart: f.start,
      absEnd: f.end,
      s: Math.max(f.start, rowStart) - rowStart,
      e: Math.min(f.end, rowEndIncl) - rowStart,
    }))
    .sort((a, b) => a.s - b.s || a.e - b.e);

  const tracks = [];
  for (const feat of overlapping) {
    let placed = false;
    for (const t of tracks) {
      if (feat.s > t.endLocal) {
        t.items.push(feat);
        t.endLocal = feat.e;
        placed = true;
        break;
      }
    }
    if (!placed) tracks.push({ endLocal: feat.e, items: [feat] });
  }
  return tracks.map((t) => t.items);
}
